import { useEffect, useState } from 'react'
import { seasonsService } from '../services/seasons.service'
import type { FilmDocument } from '@/types'

type Film = FilmDocument & { id: string }

interface UseAdjacentFilmsResult {
  prev: Film | null
  next: Film | null
  loading: boolean
}

export function useAdjacentFilms(film: Film | null): UseAdjacentFilmsResult {
  const [prev, setPrev] = useState<Film | null>(null)
  const [next, setNext] = useState<Film | null>(null)
  const [loading, setLoading] = useState(false)
  const filmId = film?.id
  const filmType = film?.type
  const seasonNumber = film?.seasonNumber

  useEffect(() => {
    if (!filmId || filmType !== 'episode') {
      setPrev(null)
      setNext(null)
      return
    }

    setLoading(true)
    setPrev(null)
    setNext(null)

    async function load() {
      if (seasonNumber === undefined) return

      const seasonFilms = await seasonsService.getFilmsBySeason(seasonNumber)
      const idx = seasonFilms.findIndex((f) => f.id === filmId)

      let prevFilm: Film | null = idx > 0 ? (seasonFilms[idx - 1] ?? null) : null
      let nextFilm: Film | null = idx < seasonFilms.length - 1 ? (seasonFilms[idx + 1] ?? null) : null

      if (!prevFilm || !nextFilm) {
        const seasons = await seasonsService.getPublishedSeasons()
        const currentSeasonIdx = seasons.findIndex((s) => s.number === seasonNumber)

        const fetches: Promise<void>[] = []

        if (!prevFilm && currentSeasonIdx > 0) {
          const prevSeason = seasons[currentSeasonIdx - 1]
          if (prevSeason) {
            fetches.push(
              seasonsService
                .getFilmsBySeason(prevSeason.number)
                .then((films) => {
                  prevFilm = films[films.length - 1] ?? null
                })
            )
          }
        }

        if (!nextFilm && currentSeasonIdx < seasons.length - 1) {
          const nextSeason = seasons[currentSeasonIdx + 1]
          if (nextSeason) {
            fetches.push(
              seasonsService
                .getFilmsBySeason(nextSeason.number)
                .then((films) => {
                  nextFilm = films[0] ?? null
                })
            )
          }
        }

        await Promise.all(fetches)
      }

      setPrev(prevFilm)
      setNext(nextFilm)
    }

    load()
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [filmId, filmType, seasonNumber])

  return { prev, next, loading }
}
